import { AlertCircle, Check, CheckCircle, ExternalLink, Info, Trash2, TriangleAlert, ZoomIn } from 'lucide-react';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '../Common/sheet';
import { Button } from '../Common/button';
import { cn } from '../../lib/utils';
import { AppNotification, NotificationCategory } from '../../types/notification';
import { useNotificationActions, useUserNotifications } from '../../stores/notificationStore';
import { SystemErrorDetailModal } from './SystemErrorDetailModal';

interface NotificationPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string;
  isSuperAdmin?: boolean;
}

const categoryConfig: Record<
  NotificationCategory,
  { icon: React.ElementType; iconClass: string; borderClass: string }
> = {
  success: {
    icon: CheckCircle,
    iconClass: 'text-emerald-500',
    borderClass: 'border-l-emerald-500',
  },
  error: {
    icon: AlertCircle,
    iconClass: 'text-destructive',
    borderClass: 'border-l-destructive',
  },
  warning: {
    icon: TriangleAlert,
    iconClass: 'text-amber-500',
    borderClass: 'border-l-amber-500',
  },
  info: {
    icon: Info,
    iconClass: 'text-blue-500',
    borderClass: 'border-l-blue-500',
  },
};

function relativeTimeAgo(isoDate: string, locale: string): string {
  const diff = (new Date(isoDate).getTime() - Date.now()) / 1000; // negative = past
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  const abs = Math.abs(diff);
  if (abs < 60) return rtf.format(Math.round(diff), 'second');
  if (abs < 3600) return rtf.format(Math.round(diff / 60), 'minute');
  if (abs < 86400) return rtf.format(Math.round(diff / 3600), 'hour');
  if (abs < 2592000) return rtf.format(Math.round(diff / 86400), 'day');
  if (abs < 31536000) return rtf.format(Math.round(diff / 2592000), 'month');
  return rtf.format(Math.round(diff / 31536000), 'year');
}

function NotificationItem({
  notification,
  onMarkRead,
  onRemove,
  onViewDetails,
}: {
  notification: AppNotification;
  onMarkRead: () => void;
  onRemove: () => void;
  onViewDetails?: () => void;
}) {
  const { t, i18n } = useTranslation();
  const config = categoryConfig[notification.category];
  const Icon = config.icon;
  const hasDetails = notification.isSystemError && (
    notification.systemErrorDetails || notification.description
  );

  const timeAgo = relativeTimeAgo(notification.createdAt, i18n.language);

  return (
    <div
      className={cn(
        'group relative flex gap-3 rounded-lg border border-border border-l-4 p-3 transition-colors',
        config.borderClass,
        notification.read ? 'bg-background opacity-60' : 'bg-card',
        notification.isSystemError && 'border-destructive/40 bg-destructive/5',
      )}
    >
      <Icon className={cn('mt-0.5 h-5 w-5 flex-shrink-0', config.iconClass)} />

      <div className="min-w-0 flex-1">
        <div className="flex items-start gap-2">
          {notification.title && (
            <p className="text-sm font-medium text-foreground leading-snug flex-1">{notification.title}</p>
          )}
          {notification.isSystemError && (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-destructive/10 text-destructive border border-destructive/20 flex-shrink-0">
              {t('notifications.systemBadge')}
            </span>
          )}
        </div>
        {notification.description && (
          <p className="mt-0.5 text-xs text-muted-foreground leading-relaxed line-clamp-2">
            {typeof notification.description === 'string'
              ? notification.description
              : JSON.stringify(notification.description)}
          </p>
        )}
        <div className="mt-1.5 flex items-center gap-3 flex-wrap">
          <p className="text-xs text-muted-foreground/60">{timeAgo}</p>
          {notification.link && (
            <Link
              to={notification.link}
              className="inline-flex items-center gap-0.5 text-xs font-medium text-primary hover:underline"
              onClick={onMarkRead}
            >
              {t('notifications.view')}
              <ExternalLink className="h-3 w-3" />
            </Link>
          )}
          {hasDetails && onViewDetails && (
            <button
              onClick={(e) => { e.stopPropagation(); onMarkRead(); onViewDetails(); }}
              className="inline-flex items-center gap-1 text-xs font-medium text-destructive hover:text-destructive/80 hover:underline transition-colors"
            >
              <ZoomIn className="h-3 w-3" />
              {t('notifications.errorDetail.viewDetails', 'View details')}
            </button>
          )}
        </div>
      </div>

      <div className="flex flex-shrink-0 items-start gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        {!notification.read && (
          <button
            onClick={onMarkRead}
            className="rounded p-1 hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
            title={t('notifications.markAsRead')}
          >
            <Check className="h-3.5 w-3.5" />
          </button>
        )}
        <button
          onClick={onRemove}
          className="rounded p-1 hover:bg-muted text-muted-foreground hover:text-destructive transition-colors"
          title={t('notifications.remove')}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>

      {!notification.read && (
        <span className="absolute right-2 top-2 h-1.5 w-1.5 rounded-full bg-primary" />
      )}
    </div>
  );
}

export const NotificationPanel: React.FC<NotificationPanelProps> = ({
  open,
  onOpenChange,
  userId,
  isSuperAdmin = false,
}) => {
  const { t } = useTranslation();
  const allNotifications = useUserNotifications(userId);
  const { markAsRead, markAllAsRead, removeNotification, clearAll } = useNotificationActions();
  const [detailNotif, setDetailNotif] = useState<AppNotification | null>(null);

  // Regular users cannot see system-level errors (backend/code errors)
  const notifications = isSuperAdmin
    ? allNotifications
    : allNotifications.filter((n) => !n.isSystemError);

  const unreadCount = notifications.filter((n) => !n.read).length;

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="right" className="flex w-96 flex-col gap-0 p-0 glass">
          {/* Header */}
          <SheetHeader className="border-b border-border/60 px-5 py-4 bg-card/80 backdrop-blur-sm">
            <div className="flex items-center justify-between pr-6">
              <div className="flex items-center gap-2">
                <SheetTitle className="text-base">
                  {t('notifications.title')}
                </SheetTitle>
                {unreadCount > 0 && (
                  <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1 text-[11px] font-semibold text-primary-foreground">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </div>
            </div>

            {notifications.length > 0 && (
              <div className="flex gap-2 pt-1">
                {unreadCount > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs text-muted-foreground"
                    onClick={() => markAllAsRead(userId)}
                  >
                    {t('notifications.markAllRead')}
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs text-muted-foreground"
                  onClick={() => clearAll(userId)}
                >
                  {t('notifications.clearAll')}
                </Button>
              </div>
            )}
          </SheetHeader>

          {/* Notification list */}
          <div className="flex-1 overflow-y-auto px-4 py-3">
            {notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
                <div className="rounded-full bg-muted p-4">
                  <Info className="h-6 w-6 text-muted-foreground" />
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground">{t('notifications.emptyTitle')}</p>
                  <p className="mt-1 text-xs text-muted-foreground">{t('notifications.emptyDescription')}</p>
                </div>
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                {notifications.map((notification) => (
                  <NotificationItem
                    key={notification.id}
                    notification={notification}
                    onMarkRead={() => markAsRead(userId, notification.id)}
                    onRemove={() => removeNotification(userId, notification.id)}
                    onViewDetails={
                      notification.isSystemError
                        ? () => setDetailNotif(notification)
                        : undefined
                    }
                  />
                ))}
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>

      {/* System error detail modal — rendered outside the Sheet so it's not clipped */}
      {detailNotif && (
        <SystemErrorDetailModal
          notification={detailNotif}
          onClose={() => setDetailNotif(null)}
        />
      )}
    </>
  );
};
